'use-client';
import * as React from 'react';
import classNames from 'clsx';
import { getDataAttributes } from '@wix/editor-elements-common-utils';
import type { IDisqusCommentsProps } from '../DisqusComments.types';
import type { IFrameEvent } from '../../../providers/useIFrame/useIFrame';
import { useIFrame } from '../../../providers/useIFrame/useIFrame';
import { getComponentProps } from '../../ConsentPolicyWrapper/viewer/utils';
import style from './style/DisqusComments.scss';
import baseIframeUrl from './assets/DisqusComments.html?resource';

const DisqusComments: React.FC<IDisqusCommentsProps> = props => {
  const {
    id: compId,
    disqusId,
    currentUrl: pageUrl,
    isConsentPolicyActive,
    onMouseEnter,
    onMouseLeave,
    className,
  } = props;
  const [height, setHeight] = React.useState('200px');
  const [render, setRender] = React.useState(false);

  React.useEffect(() => setRender(true), []);

  const iframeSrc = React.useMemo(
    () =>
      `${baseIframeUrl}?${new URLSearchParams({
        disqusId,
        pageUrl,
        compId,
        /** TL;DR;
         * 'pageTitle' & 'disqusInstanceId' are set as "undefined" here in order
         * to preserve the current "broken" behaviour we got for sites that rendered
         * this component with bolt.
         * Longer version:
         * Santa api's that were in charge of populating these variables were not
         * implemented in bolt, this led to passing these variables as `undefined`
         * when the site was rendered with bolt.
         * These `undefined` values transformed to the string value of "undefined"
         * when passed to the iframe and was set as the `identifier` of the conversation.
         * This let to having all the instances of this component in a certain site
         * sharing the same comments.
         * While this is clearly a broken behaviour, it was decided to preserve it
         * for now to since this component is no longer offered to new customers.
         */
        pageTitle: 'undefined',
        disqusInstanceId: 'undefined',
      })}`,
    [compId, disqusId, pageUrl],
  );

  const handleMessage = React.useCallback(
    ({ type, payload }: IFrameEvent) => {
      if (type === 'message' && typeof payload === 'string') {
        try {
          // error handling for valid JSON
          const json = JSON.parse(payload);
          if (json.id === compId) {
            setHeight(json.height);
          }
        } catch (ee) {
          return;
        }
      }
    },
    [compId],
  );

  const [ref] = useIFrame({ reducer: handleMessage });
  const key = `DisqusComments-${pageUrl}`;

  const baseComponentProps = getComponentProps(isConsentPolicyActive, {
    id: compId,
    className: classNames(style.root, className),
    ...getDataAttributes(props),
  });

  return (
    <div
      {...baseComponentProps}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{ height }}
    >
      {render && (
        <wix-iframe data-src={iframeSrc}>
          {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
          <iframe
            style={{ height }}
            data-src={iframeSrc}
            className={style.disqusCommentsHolder}
            key={key}
            ref={ref}
          />
          {!disqusId && (
            <div className={style.noDisqusIdMessage}>
              There was an error loading Disqus comments
            </div>
          )}
        </wix-iframe>
      )}
    </div>
  );
};

export default DisqusComments;
